import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/client';

interface GuidedPrompt {
  id: string;
  label: string;
  prompt: string;
  requires_input: boolean;
  input_placeholder: string | null;
  input_token_placeholder: string | null;
  sort_order: number;
  is_active: boolean;
}

const emptyPrompt: Omit<GuidedPrompt, 'id'> = {
  label: '',
  prompt: '',
  requires_input: false,
  input_placeholder: null,
  input_token_placeholder: null,
  sort_order: 99,
  is_active: true,
};

const AdminAIPromptsManagement: React.FC = () => {
  const [prompts, setPrompts] = useState<GuidedPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<Omit<GuidedPrompt, 'id'>>(emptyPrompt);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchPrompts = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('campaign_ai_guided_prompts')
      .select('*')
      .order('sort_order');
    if (fetchError) {
      setError(fetchError.message);
    } else {
      setPrompts(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchPrompts(); }, []);

  const handleEdit = (p: GuidedPrompt) => {
    setEditingId(p.id);
    setFormData({
      label: p.label,
      prompt: p.prompt,
      requires_input: p.requires_input,
      input_placeholder: p.input_placeholder,
      input_token_placeholder: p.input_token_placeholder,
      sort_order: p.sort_order,
      is_active: p.is_active,
    });
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData(emptyPrompt);
  };

  const handleSave = async () => {
    if (!formData.label.trim() || !formData.prompt.trim()) {
      setError('Label and prompt are required.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      label: formData.label.trim(),
      prompt: formData.prompt.trim(),
      requires_input: formData.requires_input,
      input_placeholder: formData.requires_input ? (formData.input_placeholder || null) : null,
      input_token_placeholder: formData.requires_input ? (formData.input_token_placeholder || null) : null,
      sort_order: formData.sort_order,
      is_active: formData.is_active,
      updated_at: new Date().toISOString(),
    };

    let err;
    if (editingId) {
      ({ error: err } = await supabase
        .from('campaign_ai_guided_prompts')
        .update(payload)
        .eq('id', editingId));
    } else {
      ({ error: err } = await supabase
        .from('campaign_ai_guided_prompts')
        .insert(payload));
    }

    setSaving(false);
    if (err) {
      setError(err.message);
    } else {
      setEditingId(null);
      setShowAddForm(false);
      setFormData(emptyPrompt);
      fetchPrompts();
    }
  };

  const handleToggleActive = async (p: GuidedPrompt) => {
    await supabase
      .from('campaign_ai_guided_prompts')
      .update({ is_active: !p.is_active, updated_at: new Date().toISOString() })
      .eq('id', p.id);
    fetchPrompts();
  };

  const handleDelete = async (id: string) => {
    await supabase.from('campaign_ai_guided_prompts').delete().eq('id', id);
    setDeleteConfirm(null);
    fetchPrompts();
  };

  const FormPanel = () => (
    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50 space-y-3">
      <h3 className="text-sm font-semibold text-blue-800">
        {editingId ? 'Edit prompt' : 'Add new prompt'}
      </h3>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Label (button text)</label>
        <input
          type="text"
          value={formData.label}
          onChange={e => setFormData(f => ({ ...f, label: e.target.value }))}
          placeholder="e.g. ✍️ Write another campaign"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Prompt (sent to AI)</label>
        <textarea
          value={formData.prompt}
          onChange={e => setFormData(f => ({ ...f, prompt: e.target.value }))}
          rows={4}
          placeholder="The full prompt text. Use [TOKEN] as placeholder if requires_input is enabled."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      <div className="flex gap-6 items-center">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={formData.requires_input}
            onChange={e => setFormData(f => ({ ...f, requires_input: e.target.checked }))}
            className="rounded"
          />
          Requires user input
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={e => setFormData(f => ({ ...f, is_active: e.target.checked }))}
            className="rounded"
          />
          Active
        </label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600">Sort order:</span>
          <input
            type="number"
            value={formData.sort_order}
            onChange={e => setFormData(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
            className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
          />
        </div>
      </div>

      {formData.requires_input && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Input placeholder</label>
            <input
              type="text"
              value={formData.input_placeholder || ''}
              onChange={e => setFormData(f => ({ ...f, input_placeholder: e.target.value || null }))}
              placeholder='e.g. Which role? (e.g. "Head of Content")'
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Token in prompt</label>
            <input
              type="text"
              value={formData.input_token_placeholder || ''}
              onChange={e => setFormData(f => ({ ...f, input_token_placeholder: e.target.value || null }))}
              placeholder="e.g. [ROLE]"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={handleCancelEdit}
          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading guided prompts…</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">AI Analyzer Quick Actions</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Guided action buttons shown in the AI Campaign Analyzer. Changes take effect on next modal open.
          </p>
        </div>
        {!showAddForm && !editingId && (
          <button
            onClick={() => { setShowAddForm(true); setFormData(emptyPrompt); }}
            className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Add prompt
          </button>
        )}
      </div>

      {error && !editingId && !showAddForm && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {(showAddForm && !editingId) && <div className="mb-4"><FormPanel /></div>}

      <div className="space-y-2">
        {prompts.map(p => (
          <div key={p.id}>
            {editingId === p.id ? (
              <FormPanel />
            ) : (
              <div className={`border rounded-lg p-4 ${p.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900">{p.label}</span>
                      <span className="text-xs text-gray-400">#{p.sort_order}</span>
                      {!p.is_active && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">inactive</span>
                      )}
                      {p.requires_input && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">input required</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">{p.prompt}</p>
                    {p.requires_input && p.input_token_placeholder && (
                      <p className="text-xs text-purple-500 mt-0.5">Token: {p.input_token_placeholder}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggleActive(p)}
                      className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                      {p.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => handleEdit(p)}
                      className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200 hover:border-blue-400 transition-colors"
                    >
                      Edit
                    </button>
                    {deleteConfirm === p.id ? (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="text-xs text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded border border-gray-200 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(p.id)}
                        className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:border-red-400 transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {prompts.length === 0 && !showAddForm && (
        <p className="text-sm text-gray-400 text-center py-8">No prompts yet. Add one above.</p>
      )}
    </div>
  );
};

export default AdminAIPromptsManagement;
